import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  parseISO,
  isValid,
} from 'date-fns';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  Plus,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';import { cn } from '@/app/components/ui/utils';
import { AvailabilityCalendar, TeamAvailabilityView } from '@/components/crew/availability-calendar';
import {
  useTechProfiles,
  useAvailability,
  useAssignments,
  useExpiringCerts,
} from '@/hooks/use-crew';
import type { AvailabilityStatus } from '@/lib/types';
import { useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly } from '@/lib/date-utils';
import { usePermissions } from '@/hooks/use-permissions';
const CERT_TYPE_LABELS: Record<string, string> = {
  sprat_1: 'SPRAT Level 1',
  sprat_2: 'SPRAT Level 2',
  sprat_3: 'SPRAT Level 3',
  irata_1: 'IRATA Level 1',
  irata_2: 'IRATA Level 2',
  irata_3: 'IRATA Level 3',
  first_aid: 'First Aid',
  cpr: 'CPR',
  confined_space: 'Confined Space',
  fall_protection: 'Fall Protection',
  rigging: 'Rigging',
  crane_signal: 'Crane Signal',
  hazmat: 'HAZMAT',
  osha_10: 'OSHA 10',
  osha_30: 'OSHA 30',
  twic: 'TWIC',
};

export function SchedulePage() {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const orgToday = useOrgToday();
  const [searchParams] = useSearchParams();
  const [currentMonth, setCurrentMonth] = useState(() => {
    const month = searchParams.get('month');
    if (month && /^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
      const parsedMonth = parseISO(`${month}-01`);
      if (isValid(parsedMonth)) return parsedMonth;
    }
    return parseISO(orgToday);
  });
  const [techFilter, setTechFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [certFilter, setCertFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState('');
  const [view, setView] = useState<'team' | 'calendar'>('team');

  const monthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  const techFilters: Record<string, unknown> = { page_size: 200, status: 'active' as const };
  if (certFilter && certFilter !== 'all') {
    techFilters.cert_type = certFilter;
  }

  const { data: techsData } = useTechProfiles(techFilters as Parameters<typeof useTechProfiles>[0]);
  const { data: availData } = useAvailability({
    date_from: monthStart,
    date_to: monthEnd,
    page_size: 5000,
    ...(techFilter !== 'all' ? { tech: Number(techFilter) } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter as AvailabilityStatus } : {}),
  });
  const { data: assignmentsData } = useAssignments({
    page_size: 200,
    ...(projectFilter ? { q: projectFilter } : {}),
  });
  const { data: certsData } = useExpiringCerts(60);

  const techs = techsData?.results ?? [];
  const availability = availData?.results ?? [];
  const assignments = assignmentsData?.results ?? [];
  const expiringCerts = certsData?.results ?? [];

  // Filter availability for "available now" quick filter
  const availableNowTechIds = useMemo(() => {
    return new Set(
      availability
        .filter((e) => e.date === orgToday && e.status === 'available')
        .map((e) => e.tech)
    );
  }, [availability, orgToday]);

  // Active assignments for the month
  const activeAssignments = useMemo(() => {
    return assignments.filter((a) => {
      if (a.status === 'cancelled' || a.status === 'completed') return false;
      const start = a.start_date;
      const end = a.end_date ?? '9999-12-31';
      return start <= monthEnd && end >= monthStart;
    });
  }, [assignments, monthStart, monthEnd]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Schedule</h1>
          </div>
          <p className="text-gray-600 mt-1">Team-wide scheduling and availability</p>
        </div>
        {canManageCrew && (
          <Button asChild>
            <Link to="/crew/assignments/new">
              <Plus className="h-4 w-4 mr-2" />
              New Assignment
            </Link>
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Available Today</p>
                <p className="text-2xl font-bold text-green-600">{availableNowTechIds.size}</p>
              </div>
              <Users className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Assignments</p>
                <p className="text-2xl font-bold text-blue-600">{activeAssignments.length}</p>
              </div>
              <CalendarDays className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Certs Expiring in 60 Days</p>
                <p className="text-2xl font-bold text-orange-600">{expiringCerts.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select value={techFilter} onValueChange={setTechFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technicians</SelectItem>
                {techs.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="unavailable">Unavailable</SelectItem>
                <SelectItem value="tentative">Tentative</SelectItem>
                <SelectItem value="on_project">On Project</SelectItem>
              </SelectContent>
            </Select>

            <Select value={certFilter} onValueChange={setCertFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Certs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Certifications</SelectItem>
                {Object.entries(CERT_TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="Search project..."
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Month Navigation + View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold sm:min-w-[180px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentMonth(parseISO(orgToday))}
            className="text-blue-600"
          >
            Today
          </Button>
        </div>
        <Tabs value={view} onValueChange={(v) => setView(v as 'team' | 'calendar')}>
          <TabsList>
            <TabsTrigger value="team">
              <Users className="h-4 w-4 mr-1" />
              Team
            </TabsTrigger>
            <TabsTrigger value="calendar">
              <CalendarDays className="h-4 w-4 mr-1" />
              Calendar
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Schedule Views */}
      <Card>
        <CardContent className="p-4">
          {view === 'team' ? (
            <TeamAvailabilityView
              techs={techs}
              entries={availability}
              month={currentMonth}
              onTechClick={(techId) => setTechFilter(String(techId))}
            />
          ) : (
            <AvailabilityCalendar
              entries={availability}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
            />
          )}
        </CardContent>
      </Card>

      {/* Cert Expiry Warnings */}
      {expiringCerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Certification Expiry Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {expiringCerts.map((cert) => (
                <div
                  key={cert.id}
                  className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-sm">{cert.cert_name}</p>
                    <p className="text-xs text-gray-600">
                      {cert.tech_name ?? `Tech #${cert.tech}`}
                    </p>
                  </div>
                  <Badge className="bg-orange-100 text-orange-800 text-xs">
                    Expires: {formatDateOnly(cert.expiry_date)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Assignments */}
      {activeAssignments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="h-5 w-5 text-blue-500" />
              Active Assignments This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeAssignments.slice(0, 10).map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-sm">{assignment.project_name}</p>
                    <p className="text-xs text-gray-600">
                      {assignment.tech_name ?? `Tech #${assignment.tech}`}
                      {assignment.location && ` \u2022 ${assignment.location}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge
                      className={cn(
                        'text-xs',
                        assignment.status === 'active' && 'bg-green-100 text-green-800',
                        assignment.status === 'scheduled' && 'bg-blue-100 text-blue-800'
                      )}
                    >
                      {assignment.status}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDateOnly(assignment.start_date)} —{' '}
                      {formatDateOnly(assignment.end_date, 'Ongoing')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
